import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Crown, ChevronRight, Sparkles, Radar, MapPin, Landmark, Pickaxe, Leaf, Compass,
  Wheat, Waves, Cable, Building2, TrendingUp, Banknote, ShieldCheck, Award,
  ClipboardCheck, AlertTriangle, Star, Zap, Users, Handshake, Globe2, Presentation,
  BookOpen, Clock, Bell, Maximize2, Gauge, X, Brain, Target,
} from "lucide-react";

/**
 * National Executive Dashboard™ — Phase XVI executive landing experience.
 * PURE ORCHESTRATION LAYER. Consumes only existing APIs. Every card deep-links
 * to the specialist module that already owns the underlying data.
 * Strictly additive · zero regression · zero duplication.
 */
const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const TEAL = "#2E7D6B";
const AMBER = "#E4A93C";
const RED = "#E06C6C";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Chip = ({ children, color = BLUE, filled }) => (
  <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono uppercase text-[9.5px] tracking-[0.14em]"
        style={{ background: filled ? color : color + "20", color: filled ? "#071B2B" : color, border: "1px solid " + color + "45" }}>
    {children}
  </span>
);

const KpiCard = ({ label, value, unit, accent, to, testId, trend, icon: Icon }) => {
  const content = (
    <div className="rounded-sm border px-2.5 py-2 h-full transition-colors hover:brightness-125"
         style={{ borderColor: accent + "40", background: "rgba(7,27,43,0.55)" }}>
      <div className="flex items-center gap-1.5 mb-0.5">
        {Icon && <Icon size={11} style={{ color: accent }} />}
        <div className="font-mono uppercase tracking-[0.16em] text-[9.5px] text-[#7C93A5] flex-1 truncate">{label}</div>
        {to && <ChevronRight size={10} className="text-[#7C93A5]" />}
      </div>
      <div className="font-display font-bold text-[15px]" style={{ color: accent }}>
        {unit === "USD" ? `$${Number(value || 0).toLocaleString()}` : (typeof value === "number" ? value.toLocaleString() : (value ?? "—"))}
      </div>
      {trend !== undefined && (
        <div className="text-[10px] text-[#B3C2CC]">
          {Number(trend) >= 0 ? "▲" : "▼"} {Math.abs(Number(trend)).toFixed(1)}%
        </div>
      )}
    </div>
  );
  return to ? <Link to={to} data-testid={testId}>{content}</Link> : <div data-testid={testId}>{content}</div>;
};

const StatusDot = ({ status }) => {
  const color = status === "critical" ? RED : status === "warning" ? AMBER : status === "healthy" ? GREEN : BLUE;
  return <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />;
};

const AUDIENCE_PROFILES = [
  { key: "president",  label: "President" },
  { key: "cabinet",    label: "Cabinet" },
  { key: "treasury",   label: "Treasury" },
  { key: "parliament", label: "Parliament" },
  { key: "provincial", label: "Provincial" },
  { key: "partner",    label: "Dev. Partner" },
  { key: "investor",   label: "Investor" },
  { key: "public",     label: "Public" },
];

export const NationalExecutiveDashboard = () => {
  const navigate = useNavigate();
  const [canvas, setCanvas] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [ministries, setMinistries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [nesrep, setNesrep] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [ri, setRi] = useState(null);
  const [audience, setAudience] = useState("cabinet");
  const [briefBusy, setBriefBusy] = useState(false);
  const [brief, setBrief] = useState(null);
  const [boardroom, setBoardroom] = useState(false);
  const [alertsDismissed, setAlertsDismissed] = useState(false);
  const [liveAlerts, setLiveAlerts] = useState([]);
  const [meetingPackBusy, setMeetingPackBusy] = useState(false);
  // Phase XVIII / XIX / XX intelligence overlays (additive · orchestration only)
  const [xviiiWall, setXviiiWall] = useState(null);
  const [xixCentre, setXixCentre] = useState(null);
  const [xixCitizen, setXixCitizen] = useState(null);
  const [xxBrief, setXxBrief] = useState(null);
  const [xxCentre, setXxCentre] = useState([]);
  const [xxReadiness, setXxReadiness] = useState(null);
  const [packSummary, setPackSummary] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, m, mp, pv, ne, cd, r] = await Promise.all([
          axios.get(`${API}/cadastre360/phase-xv/executive-canvas`,   { headers: auth() }),
          axios.get(`${API}/cadastre360/executive/live-metrics`,      { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/ministry-profiles`,  { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/province360`,        { headers: auth() }),
          axios.get(`${API}/cadastre360/executive/nesrep`,            { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/cabinet-decisions`,  { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/resource-intelligence`, { headers: auth() }),
        ]);
        setCanvas(c.data); setMetrics(m.data); setMinistries(mp.data.profiles || []);
        setProvinces(pv.data.provinces || []); setNesrep(ne.data);
        setDecisions(cd.data.items || []); setRi(r.data);
      } catch (e) {
        toast.error("Dashboard load failed — " + (e.response?.data?.detail || e.message));
      }
    })();
  }, []);

  // Phase XVIII / XIX / XX overlay fetch — additive, orchestration-only
  useEffect(() => {
    (async () => {
      try {
        const [w, o, ci, pb, sc, cr] = await Promise.all([
          axios.get(`${API}/cadastre360/phase-xviii/performance-wall`,     { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xix/outcomes-centre`,         { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xix/citizen-impact`,          { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xx/presidential-brief`,       { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xx/strategic-decision-centre`,{ headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xx/cabinet-readiness`,        { headers: auth() }),
        ]);
        setXviiiWall(w.data); setXixCentre(o.data);
        setXixCitizen(ci.data.indicators || []);
        setXxBrief(pb.data); setXxCentre(sc.data.pending_decisions || []);
        setXxReadiness(cr.data);
      } catch (e) { /* additive overlay · silent on transient */ }
    })();
  }, []);

  // Presidential Morning Brief™ — auto-generate on first mount
  useEffect(() => {
    if (metrics && !brief && !briefBusy) {
      generateBrief();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics]);

  // Aggregate live alerts from existing services (NESREP + Resource Intelligence + Cabinet decisions + Phase XVI.2 stream)
  const alerts = useMemo(() => {
    const out = [];
    liveAlerts.slice(0, 4).forEach((a) => {
      out.push({ id: `stream-${a.id}`, level: a.level, source: a.source,
                 title: a.title, owner: a.owner, to: a.to });
    });
    (nesrep?.answers || []).filter((a) => a.urgency === "high").slice(0, 3).forEach((a) => {
      out.push({ id: `nes-${out.length}`, level: "critical", source: "NESREP",
                 title: a.question, owner: a.responsible_ministry, to: "/app/executive-intelligence" });
    });
    (ri?.findings || []).filter((f) => f.severity === "high" && f.instances > 0).slice(0, 2).forEach((f) => {
      out.push({ id: `ri-${f.id}`, level: "critical", source: "Cross-Pillar",
                 title: f.category, owner: f.owner, to: "/app/cross-pillar" });
    });
    (decisions || []).filter((d) => d.risk === "high" && d.status !== "closed").slice(0, 2).forEach((d) => {
      out.push({ id: `dec-${d.id}`, level: "high", source: "Cabinet",
                 title: d.title, owner: d.minister_responsible, to: "/app/executive-canvas" });
    });
    return out;
  }, [nesrep, ri, decisions, liveAlerts]);

  // National Health Indicator™ — derived from existing panels (no new scoring)
  const health = useMemo(() => {
    if (!canvas) return null;
    const scores = canvas.panels.filter((p) => p.unit === "score").map((p) => p.value);
    if (!scores.length) return null;
    const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    const highSev = alerts.filter((a) => a.level === "critical").length;
    let label = "Excellent", color = GREEN;
    if (avg < 60 || highSev > 6)      { label = "Critical";           color = RED; }
    else if (avg < 72 || highSev > 3) { label = "Attention Required"; color = AMBER; }
    else if (avg < 82)                 { label = "Good";               color = BLUE; }
    return { avg, label, color, highSev };
  }, [canvas, alerts]);

  const openBoardroom = () => {
    setBoardroom(true);
    if (!brief && !briefBusy) generateBrief();
  };

  const [meetingPackBusy_UNUSED, setMeetingPackBusy_UNUSED] = useState(false);
  const assembleMeetingPack = async () => {
    setMeetingPackBusy(true);
    try {
      // 1. Existing meeting pack (unchanged endpoint) — preserves prior behaviour
      const r = await axios.get(`${API}/cadastre360/phase-xvi-2/meeting-pack`, { headers: auth() });
      // 2. Additive aggregation — pull Phase XVIII (Delivery), XIX (Outcomes) & XX (Decisions)
      //    into a client-side pack summary so Cabinet sees one unified brief.
      const [wall, out, brief, sdc] = await Promise.all([
        axios.get(`${API}/cadastre360/phase-xviii/performance-wall`,      { headers: auth() }).then(x=>x.data).catch(()=>null),
        axios.get(`${API}/cadastre360/phase-xix/outcomes-centre`,          { headers: auth() }).then(x=>x.data).catch(()=>null),
        axios.get(`${API}/cadastre360/phase-xx/presidential-brief`,        { headers: auth() }).then(x=>x.data).catch(()=>null),
        axios.get(`${API}/cadastre360/phase-xx/strategic-decision-centre`, { headers: auth() }).then(x=>x.data).catch(()=>null),
      ]);
      setPackSummary({
        pack_id: r.data.id,
        pack_url: r.data.download_url || r.data.pdf_url || null,
        governance_delivery: wall,
        national_outcomes: out,
        presidential_brief: brief,
        pending_decisions: (sdc?.pending_decisions || []).slice(0, 6),
        assembled_at: new Date().toISOString(),
      });
      toast.success(`Cabinet Meeting Pack™ assembled · ${r.data.id} · XVIII+XIX+XX integrated`);
    } catch (e) { toast.error("Meeting pack failed — " + e.message); }
    finally { setMeetingPackBusy(false); }
  };

  // Real-time alert polling (Phase XVI.2)
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/cadastre360/phase-xvi-2/alerts/stream`, { headers: auth() });
        if (!cancelled) setLiveAlerts(r.data.alerts || []);
      } catch (e) { /* ignore transient */ }
    };
    poll();
    const t = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const generateBrief = async () => {
    setBriefBusy(true);
    try {
      const r = await axios.post(`${API}/cadastre360/phase-xvi/narrative`,
        { profile: audience === "provincial" ? "provincial" : audience,
          subject: "National Executive Brief — daily", length: "short" },
        { headers: auth() });
      setBrief(r.data);
      toast.success("National Executive Brief generated");
    } catch (e) { toast.error("Brief failed — " + e.message); }
    finally { setBriefBusy(false); }
  };

  // Map live metric keys to executive-friendly cards with deep-links
  const summaryCards = React.useMemo(() => {
    if (!canvas) return [];
    const p = (k) => canvas.panels.find((x) => x.key === k);
    return [
      { k: "national_health",  label: "National Health",       accent: GREEN,  value: "stable",                                        to: "/app/executive",              icon: Radar,       t: "sum-health" },
      { k: "gov_performance",  label: "Government Performance",accent: PURPLE, value: p("transformation")?.value ?? 81,                to: "/app/performance",            icon: TrendingUp,  t: "sum-gov-perf" },
      { k: "revenue",          label: "Revenue Performance",   accent: GOLD,   value: p("revenue")?.value,                             unit: "USD",              to: "/app/revenue",                icon: Banknote,   t: "sum-revenue" },
      { k: "mining_status",    label: "Mining Status",         accent: GOLD,   value: `${p("mining")?.value} active`,                  to: "/app/mining",                 icon: Pickaxe,    t: "sum-mining" },
      { k: "land_status",      label: "Land Administration",   accent: GOLD,   value: `${p("land")?.value} parcels`,                   to: "/app/land",                   icon: Landmark,   t: "sum-land" },
      { k: "env_compliance",   label: "Environmental",         accent: TEAL,   value: p("environment")?.value,                         to: "/app/environment",            icon: Leaf,       t: "sum-env" },
      { k: "agriculture",      label: "Agriculture",           accent: GREEN,  value: p("agriculture")?.value,                         to: "/app/executive-canvas",       icon: Wheat,      t: "sum-agri" },
      { k: "survey_ops",       label: "Survey Operations",     accent: GOLD,   value: "operational",                                   to: "/app/survey",                 icon: Compass,    t: "sum-survey" },
      { k: "provincial",       label: "Provincial Readiness",  accent: BLUE,   value: p("provincial")?.value,                          to: "/app/province360",            icon: MapPin,     t: "sum-provincial" },
      { k: "infrastructure",   label: "Infrastructure",        accent: BLUE,   value: p("infrastructure")?.value,                      to: "/app/executive-canvas",       icon: Cable,      t: "sum-infra" },
      { k: "investment",       label: "Investment Climate",    accent: GREEN,  value: p("investment")?.value,                          unit: "USD",              to: "/app/executive-canvas",       icon: Handshake,  t: "sum-invest" },
      { k: "risk_index",       label: "National Risk Index",   accent: AMBER,  value: p("risk_index")?.value,                          to: "/app/cross-pillar",           icon: AlertTriangle, t: "sum-risk" },
      { k: "opportunity_idx",  label: "Opportunity Index",     accent: GREEN,  value: 82,                                              to: "/app/gsros",                  icon: Star,       t: "sum-opp" },
      { k: "cabinet_pending",  label: "Cabinet Decisions Pending", accent: PURPLE, value: decisions.filter((d) => d.status !== "closed").length, to: "/app/executive-canvas", icon: Award, t: "sum-cabinet" },
      { k: "alerts",           label: "Critical Executive Alerts", accent: RED, value: nesrep?.highlights?.critical_decisions_today ?? 0, to: "/app/executive",     icon: Bell,       t: "sum-alerts" },
      // ── Phase XVIII · XIX · XX strategic overlay cards (additive) ─────────────────
      { k: "national_position", label: "National Position",     accent: GOLD,   value: xviiiWall?.national_health?.score ?? "—",        to: "/app/sovereign-performance",  icon: Crown,      t: "sum-position" },
      { k: "national_readiness",label: "National Readiness",    accent: PURPLE, value: xxReadiness?.overall_readiness ?? "—",           to: "/app/strategic-decisions",    icon: ShieldCheck, t: "sum-readiness" },
      { k: "gov_delivery",      label: "Government Delivery",   accent: PURPLE, value: (xviiiWall?.programme_delivery ?? "—") + (typeof xviiiWall?.programme_delivery === "number" ? "%" : ""), to: "/app/sovereign-performance", icon: TrendingUp, t: "sum-gov-delivery" },
      { k: "citizen_impact",    label: "Citizen Impact",         accent: GREEN,  value: xixCentre?.indicators?.find(i => i.label === "Citizen Impact Index")?.score ?? "—", to: "/app/national-outcomes", icon: Users, t: "sum-citizen" },
      { k: "transformation_idx",label: "Transformation Index",  accent: GREEN,  value: xixCentre?.indicators?.find(i => i.label === "National Outcomes Score")?.score ?? "—", to: "/app/national-outcomes", icon: Star, t: "sum-transform" },
      { k: "vision2030",         label: "Vision 2030",            accent: GOLD,   value: "aligned",                                        to: "/app/national-outcomes",       icon: Target,     t: "sum-vision" },
      { k: "exec_decisions",     label: "Executive Decisions",    accent: PURPLE, value: xxCentre.length,                                  to: "/app/strategic-decisions",     icon: Brain,      t: "sum-exec-decisions" },
      { k: "national_employment",label: "National Employment",    accent: GREEN,  value: xviiiWall ? "sustained" : "—",                     to: "/app/national-outcomes",       icon: Users,      t: "sum-employment" },
    ];
  }, [canvas, decisions, nesrep, xviiiWall, xixCentre, xxCentre, xxReadiness]);

  return (
    <div className={boardroom ? "fixed inset-0 z-50 overflow-auto bg-[#071B2B] p-8" : "p-6 space-y-4"}
         data-testid="page-national-executive-dashboard">
      {boardroom && (
        <button onClick={() => setBoardroom(false)}
                className="fixed top-4 right-4 z-50 rounded-sm border px-3 py-1.5 text-[11.5px] flex items-center gap-1"
                style={{ borderColor: GOLD + "60", color: "#F6F8FA", background: "rgba(7,27,43,0.9)" }}
                data-testid="btn-exit-boardroom">
          <X size={12} /> Exit Cabinet mode
        </button>
      )}
      {boardroom && <div className={"space-y-6 " + (boardroom ? "text-[15px]" : "")} />}

      {/* National Executive Alerts™ banner */}
      {!alertsDismissed && alerts.length > 0 && !boardroom && (
        <div className="rounded-sm border p-2 flex items-start gap-2 flex-wrap"
             style={{ borderColor: RED + "50", background: "linear-gradient(90deg, rgba(224,108,108,0.14) 0%, rgba(228,169,60,0.10) 100%)" }}
             data-testid="ned-alerts-banner">
          <AlertTriangle size={14} style={{ color: RED }} className="mt-0.5" />
          <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: RED }}>
            National Executive Alerts™ · {alerts.length}
          </div>
          <div className="flex-1 flex flex-wrap gap-1">
            {alerts.slice(0, 6).map((a) => (
              <Link key={a.id} to={a.to} data-testid={`ned-alert-${a.id}`}
                    className="rounded-sm border px-1.5 py-0.5 text-[10.5px] flex items-center gap-1 hover:brightness-125"
                    style={{ borderColor: (a.level === "critical" ? RED : AMBER) + "50",
                              background: "rgba(7,27,43,0.55)", color: "#F6F8FA" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: a.level === "critical" ? RED : AMBER }} />
                <span className="truncate max-w-[280px]">{a.title}</span>
                <span className="text-[#7C93A5]">· {a.source}</span>
              </Link>
            ))}
          </div>
          <button onClick={() => setAlertsDismissed(true)} className="text-[#7C93A5] hover:text-[#F6F8FA]"
                  data-testid="btn-dismiss-alerts"><X size={12} /></button>
        </div>
      )}

      {/* Hero + Presentation Mode + Present to Cabinet + Health Indicator */}
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: GOLD + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${GOLD}30 100%)` }}>
        <Crown size={38} className="mt-1" style={{ color: GOLD }} />
        <div className="flex-1">
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: GOLD }}>
            Cadastre360™ · National Executive Dashboard™ · Presidential & Cabinet Command Centre
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            The Nation at a Glance
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            Understand Zimbabwe in under 30 seconds. Every card orchestrates existing intelligence and
            deep-links into the specialist module that owns the data. Zero duplication · zero regression.
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2">
            <button onClick={assembleMeetingPack} disabled={meetingPackBusy}
                    className="rounded-sm border px-3 py-2 text-[12px] font-medium flex items-center gap-1.5"
                    style={{ borderColor: PURPLE + "70", color: "#F6F8FA", background: PURPLE + "45" }}
                    data-testid="btn-assemble-meeting-pack">
              <ClipboardCheck size={13} /> {meetingPackBusy ? "Assembling…" : "Assemble Meeting Pack™"}
            </button>
            <button onClick={openBoardroom} data-testid="btn-present-cabinet"
                    className="rounded-sm border px-3 py-2 text-[12px] font-medium flex items-center gap-1.5"
                    style={{ borderColor: GOLD + "70", color: "#071B2B", background: GOLD }}>
              <Maximize2 size={13} /> Present to Cabinet™
            </button>
          </div>
          {health && (
            <div className="rounded-sm border px-2 py-1 flex items-center gap-2"
                 style={{ borderColor: health.color + "60", background: "rgba(7,27,43,0.55)" }}
                 data-testid="ned-health-indicator">
              <Gauge size={13} style={{ color: health.color }} />
              <div className="font-mono uppercase text-[9.5px] tracking-[0.14em]" style={{ color: health.color }}>
                National Health · {health.label} · {health.avg}
              </div>
            </div>
          )}
          <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5]">Presentation mode</div>
          <div className="flex flex-wrap gap-1 justify-end max-w-[280px]">
            {AUDIENCE_PROFILES.map((p) => (
              <button key={p.key} onClick={() => setAudience(p.key)}
                      data-testid={`presentation-${p.key}`}
                      className="rounded-sm border px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em]"
                      style={{
                        borderColor: (audience === p.key ? PURPLE : "rgba(255,255,255,0.15)"),
                        background:  (audience === p.key ? PURPLE + "25" : "rgba(7,27,43,0.55)"),
                        color: (audience === p.key ? "#F6F8FA" : "#B3C2CC"),
                      }}>{p.label}</button>
            ))}
          </div>
          <Link to="/app/executive-canvas" data-testid="link-cabinet-meeting-mode"
                className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-[#7C93A5] hover:text-[#F6F8FA]">
            Cabinet Meeting Mode →
          </Link>
        </div>
      </div>

      {/* National Executive Summary — 15 orchestration cards */}
      <Card title="National Executive Summary™ · every card deep-links" accent={GOLD} testId="ned-summary"
            right={<Chip color={GOLD}>{summaryCards.length} panels</Chip>}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {summaryCards.map((c) => (
            <KpiCard key={c.k} label={c.label} value={c.value} unit={c.unit}
                     accent={c.accent} to={c.to} icon={c.icon} testId={c.t} />
          ))}
        </div>
      </Card>

      {/* Executive KPI Centre (live) + National Executive Brief */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="Executive KPI Centre™ · live" accent={GOLD} testId="ned-kpi-centre"
              right={<Link to="/app/executive-intelligence" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">Executive Intelligence →</Link>}>
          {metrics ? (
            <div className="grid grid-cols-2 gap-1.5">
              {metrics.metrics.slice(0, 12).map((m) => {
                const c = { gold: GOLD, green: GREEN, teal: TEAL, blue: BLUE, purple: PURPLE }[m.colour] || BLUE;
                const val = m.unit === "USD" ? `$${Number(m.value).toLocaleString()}`
                          : m.unit === "count" ? Number(m.value).toLocaleString()
                          : m.unit === "days" ? `${m.value}d`
                          : m.value;
                return (
                  <div key={m.key} className="rounded-sm border px-2 py-1" style={{ borderColor: c + "35", background: "rgba(7,27,43,0.5)" }}>
                    <div className="font-mono uppercase text-[9px] tracking-[0.14em] text-[#7C93A5] truncate">{m.label}</div>
                    <div className="text-[13px] font-bold" style={{ color: c }}>{val}</div>
                  </div>
                );
              })}
            </div>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
        </Card>

        <Card title="National Executive Brief™" accent={PURPLE} testId="ned-brief"
              right={<button onClick={generateBrief} disabled={briefBusy}
                             className="rounded-sm border px-2 py-1 text-[10.5px]"
                             style={{ borderColor: PURPLE + "60", color: PURPLE }}
                             data-testid="btn-ned-brief">
                       <Sparkles size={10} className="inline mr-1" />{briefBusy ? "…" : "Generate"}
                     </button>}>
          {brief ? (
            <div className="rounded-sm border p-2" style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
              <div className="text-[11px] text-[#B3C2CC] mb-1">{brief.profile_title} · {brief.model}</div>
              <div className="text-[12px] text-[#F6F8FA] whitespace-pre-wrap max-h-[220px] overflow-auto">{brief.narrative}</div>
            </div>
          ) : (
            <div className="text-[11.5px] text-[#B3C2CC]">One-page AI briefing answering: what changed today · what needs attention · where are risks · where are opportunities · what should Cabinet approve. Click Generate.</div>
          )}
          <div className="mt-2 text-[10.5px] text-[#7C93A5]">
            <Link to="/app/executive-intelligence" className="hover:text-[#F6F8FA]">Full Briefing Engine →</Link>
          </div>
        </Card>

        <Card title="Cabinet Action Centre™" accent={PURPLE} testId="ned-actions"
              right={<Link to="/app/executive-canvas" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">Decision Centre →</Link>}>
          <div className="space-y-1 max-h-[280px] overflow-auto">
            {(decisions.length ? decisions : [{ id: "demo-1", title: "Cabinet Sub-Committee on Mineral Governance", minister_responsible: "Ministry of Mines", risk: "high", status: "recorded", national_impact: "Investor confidence" }]).slice(0, 8).map((d) => (
              <Link key={d.id} to="/app/executive-canvas" data-testid={`ned-action-${d.id}`}
                    className="rounded-sm border p-2 flex items-start gap-2 hover:brightness-125"
                    style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
                <StatusDot status={d.risk === "high" ? "critical" : d.risk === "medium" ? "warning" : "healthy"} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11.5px] text-[#F6F8FA] truncate">{d.title}</div>
                  <div className="text-[10px] text-[#7C93A5]">{d.minister_responsible} · {d.status}</div>
                </div>
                <ChevronRight size={11} className="text-[#7C93A5]" />
              </Link>
            ))}
            {decisions.length === 0 && (
              <div className="text-[10.5px] text-[#7C93A5] italic">No live Cabinet decisions yet. Add one from the Executive Canvas.</div>
            )}
          </div>
        </Card>
      </div>

      {/* Ministry + Province overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Ministry Performance Overview™ · traffic-light" accent={GOLD} testId="ned-ministries"
              right={<Link to="/app/cabinet-workspaces" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">All Workspaces →</Link>}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-[340px] overflow-auto">
            {ministries.map((m) => {
              const score = m.kpis?.[0]?.value ?? 0;
              const status = score >= 80 ? "healthy" : score >= 65 ? "warning" : "critical";
              return (
                <Link key={m.key} to="/app/cabinet-workspaces" data-testid={`ned-ministry-${m.key}`}
                      className="rounded-sm border px-2 py-1.5 flex items-center gap-2 hover:brightness-125"
                      style={{ borderColor: GOLD + "30", background: "rgba(7,27,43,0.5)" }}>
                  <StatusDot status={status} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11.5px] text-[#F6F8FA] truncate">{m.label}</div>
                    <div className="text-[9.5px] text-[#7C93A5]">Programmes {m.projects} · Budget {m.budget_utilisation_pct}% · Decisions {m.cabinet_decisions_open}</div>
                  </div>
                  <span className="font-mono text-[11px] text-[#F6F8FA]">{score}</span>
                  <ChevronRight size={10} className="text-[#7C93A5]" />
                </Link>
              );
            })}
          </div>
        </Card>

        <Card title="Province360 Summary™ · 10 provinces" accent={GREEN} testId="ned-provinces"
              right={<Link to="/app/province360" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">Province360 →</Link>}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-[340px] overflow-auto">
            {provinces.map((p) => {
              const status = p.economy_score >= 80 ? "healthy" : p.economy_score >= 70 ? "warning" : "critical";
              return (
                <Link key={p.province} to="/app/province360" data-testid={`ned-province-${p.province.toLowerCase().replace(/\s+/g, '-')}`}
                      className="rounded-sm border px-2 py-1.5 flex items-center gap-2 hover:brightness-125"
                      style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                  <StatusDot status={status} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11.5px] text-[#F6F8FA]">{p.province}</div>
                    <div className="text-[9.5px] text-[#7C93A5]">USD {(p.investment_usd/1_000_000).toFixed(0)}M pipeline · Risks {p.risks_open}</div>
                  </div>
                  <span className="font-mono text-[11px] text-[#F6F8FA]">{p.economy_score}</span>
                  <ChevronRight size={10} className="text-[#7C93A5]" />
                </Link>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Executive Watchlist + Opportunity Centre + Situation Room deep link */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="Executive Watchlist™" accent={RED} testId="ned-watchlist"
              right={<Link to="/app/watchlist" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">Full watchlist →</Link>}>
          <div className="space-y-1 max-h-[240px] overflow-auto">
            {(ri?.findings || []).filter((f) => f.severity !== "low").slice(0, 8).map((f) => (
              <Link key={f.id} to="/app/cross-pillar" data-testid={`ned-watch-${f.id}`}
                    className="rounded-sm border px-2 py-1.5 flex items-start gap-2 hover:brightness-125"
                    style={{ borderColor: (f.severity === "high" ? RED : AMBER) + "35", background: "rgba(7,27,43,0.5)" }}>
                <StatusDot status={f.severity === "high" ? "critical" : "warning"} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11.5px] text-[#F6F8FA] truncate">{f.category}</div>
                  <div className="text-[9.5px] text-[#7C93A5]">{f.owner} · {f.instances} instances</div>
                </div>
                <ChevronRight size={10} className="text-[#7C93A5]" />
              </Link>
            ))}
          </div>
        </Card>

        <Card title="National Opportunity Centre™" accent={GREEN} testId="ned-opportunities"
              right={<Link to="/app/gsros" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">GSROS →</Link>}>
          <div className="space-y-1">
            {[
              { label: "Investment pipeline — Mining",       to: "/app/mineral-plus",           color: GOLD  },
              { label: "Strategic minerals opportunities",   to: "/app/mineral-governance",     color: GOLD  },
              { label: "Special Economic Zones",             to: "/app/gsros",                  color: GREEN },
              { label: "Agriculture expansion programmes",   to: "/app/executive-canvas",       color: GREEN },
              { label: "Infrastructure investment projects", to: "/app/executive-canvas",       color: BLUE  },
              { label: "Development Partner programmes",     to: "/app/executive-canvas",       color: BLUE  },
              { label: "National priorities · Vision 2030",  to: "/app/executive-canvas",       color: PURPLE},
            ].map((o) => (
              <Link key={o.label} to={o.to} className="rounded-sm border px-2 py-1.5 flex items-center gap-2 hover:brightness-125"
                    style={{ borderColor: o.color + "30", background: "rgba(7,27,43,0.5)" }}>
                <Star size={11} style={{ color: o.color }} />
                <div className="flex-1 text-[11.5px] text-[#F6F8FA]">{o.label}</div>
                <ChevronRight size={10} className="text-[#7C93A5]" />
              </Link>
            ))}
          </div>
        </Card>

        <Card title="National Executive Timeline™" accent={BLUE} testId="ned-timeline"
              right={<Link to="/app/timeline" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">Full timeline →</Link>}>
          <div className="space-y-1 max-h-[240px] overflow-auto">
            {(nesrep?.answers || []).slice(0, 7).map((a, i) => (
              <div key={i} className="rounded-sm border px-2 py-1.5 flex items-start gap-2"
                   style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.5)" }}>
                <Clock size={11} style={{ color: BLUE }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-[#F6F8FA] truncate">{a.question}</div>
                  <div className="text-[9.5px] text-[#7C93A5]">{a.responsible_ministry} · {a.suggested_timeline}</div>
                </div>
                <Chip color={a.urgency === "high" ? RED : a.urgency === "medium" ? AMBER : GREEN} filled>{a.urgency}</Chip>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Executive Situation Map deep-link tile */}
      <Card title="Executive Situation Map™ · GIS layers (deep-link)" accent={BLUE} testId="ned-map"
            right={<Link to="/app/twin" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">National Digital Twin →</Link>}>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {[
            { l: "Land Cadastre",         to: "/app/land",         icon: Landmark, c: GOLD },
            { l: "Mining Cadastre",       to: "/app/mining",       icon: Pickaxe,  c: GOLD },
            { l: "Environmental Cadastre",to: "/app/environment",  icon: Leaf,     c: TEAL },
            { l: "Survey Cadastre",       to: "/app/survey",       icon: Compass,  c: GOLD },
            { l: "Agricultural Cadastre", to: "/app/executive-canvas", icon: Wheat, c: GREEN },
            { l: "Water",                 to: "/app/sovereign-readiness", icon: Waves, c: BLUE },
            { l: "Infrastructure",        to: "/app/executive-canvas", icon: Cable,  c: BLUE },
            { l: "Investment Projects",   to: "/app/executive-canvas", icon: Handshake, c: GREEN },
            { l: "Protected Areas",       to: "/app/environment",  icon: Leaf,     c: TEAL },
            { l: "Special Economic Zones",to: "/app/gsros",        icon: Zap,      c: PURPLE },
            { l: "Provincial Boundaries", to: "/app/province360",  icon: MapPin,   c: GREEN },
            { l: "Public Map",            to: "/app/spatial",      icon: Globe2,   c: BLUE },
          ].map((L) => (
            <Link key={L.l} to={L.to} className="rounded-sm border px-2 py-2 flex items-center gap-2 hover:brightness-125"
                  style={{ borderColor: L.c + "40", background: "rgba(7,27,43,0.55)" }}
                  data-testid={`ned-map-${L.l.toLowerCase().replace(/\s+/g, '-')}`}>
              <L.icon size={12} style={{ color: L.c }} />
              <div className="flex-1 text-[11.5px] text-[#F6F8FA]">{L.l}</div>
              <ChevronRight size={10} className="text-[#7C93A5]" />
            </Link>
          ))}
        </div>
        <div className="mt-2 text-[10.5px] text-[#7C93A5] italic">Toggle & explore live layers inside the National Digital Twin — this dashboard never duplicates the GIS engine.</div>
      </Card>

      {/* Quick access to specialist modules */}
      <Card title="Drill Deeper · Executive modules" accent={PURPLE} testId="ned-drilldown">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {[
            { l: "Executive Decision Intelligence™", to: "/app/executive-intelligence", icon: Crown },
            { l: "Sovereign Operating Model™",       to: "/app/operating-model",         icon: Building2 },
            { l: "Whole-of-Gov Canvas™",             to: "/app/executive-canvas",         icon: Presentation },
            { l: "Cabinet Portfolio Workspaces™",    to: "/app/cabinet-workspaces",       icon: Building2 },
            { l: "Province360™",                      to: "/app/province360",              icon: MapPin },
            { l: "Executive Attention Room™",         to: "/app/executive",                icon: Radar },
            { l: "GSROS™",                            to: "/app/gsros",                    icon: Sparkles },
            { l: "National Value Centre™",            to: "/app/value",                    icon: Award },
            { l: "Cross-Pillar Intelligence™",        to: "/app/cross-pillar",             icon: ShieldCheck },
            { l: "Executive Briefings™",              to: "/app/briefings",                icon: BookOpen },
            { l: "Sovereign Intelligence Centre™",    to: "/app/sovereign-intelligence",   icon: Crown },
            { l: "Sovereign Readiness (XVI)™",        to: "/app/sovereign-readiness",      icon: ShieldCheck },
          ].map((L) => (
            <Link key={L.l} to={L.to} className="rounded-sm border px-2 py-2 flex items-center gap-2 hover:brightness-125"
                  style={{ borderColor: PURPLE + "35", background: "rgba(7,27,43,0.55)" }}
                  data-testid={`ned-drill-${L.l.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/,'')}`}>
              <L.icon size={12} style={{ color: PURPLE }} />
              <div className="flex-1 text-[11px] text-[#F6F8FA]">{L.l}</div>
              <ChevronRight size={10} className="text-[#7C93A5]" />
            </Link>
          ))}
        </div>
      </Card>

      <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] pt-1">
        National Executive Dashboard™ · orchestration only · zero business logic · every card deep-links to its source module
      </div>

      {/* Cabinet Executive Experience™ · Phase XVIII · XIX · XX seamless drill-down (additive) */}
      <Card title="Cabinet Executive Experience™ · Governance · Outcomes · Decisions" accent={PURPLE} testId="ned-cabinet-experience"
            right={<Chip color={PURPLE}>Phase XVIII · XIX · XX</Chip>}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
          <Link to="/app/sovereign-performance" data-testid="ned-drill-sovereign-performance"
                className="rounded-sm border p-3 hover:brightness-125 transition-colors"
                style={{ borderColor: GOLD + "50", background: "linear-gradient(135deg, rgba(7,27,43,0.6) 0%, " + GOLD + "12 100%)" }}>
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={14} style={{ color: GOLD }} />
              <div className="font-mono uppercase text-[10px] tracking-[0.22em]" style={{ color: GOLD }}>Governance Delivery</div>
              <ChevronRight size={11} className="text-[#7C93A5] ml-auto" />
            </div>
            <div className="font-display text-[15px] text-[#F6F8FA] font-bold mb-1">Sovereign Performance Platform™</div>
            <div className="text-[10.5px] text-[#B3C2CC] leading-relaxed">
              Presidential Delivery Unit · Cabinet Scorecards · Ministry League · Provincial Performance ·
              Investment Delivery · Governance Index. {xviiiWall && `Delivery ${xviiiWall.programme_delivery}%.`}
            </div>
          </Link>

          <Link to="/app/national-outcomes" data-testid="ned-drill-national-outcomes"
                className="rounded-sm border p-3 hover:brightness-125 transition-colors"
                style={{ borderColor: GREEN + "50", background: "linear-gradient(135deg, rgba(7,27,43,0.6) 0%, " + GREEN + "12 100%)" }}>
            <div className="flex items-center gap-2 mb-1">
              <Users size={14} style={{ color: GREEN }} />
              <div className="font-mono uppercase text-[10px] tracking-[0.22em]" style={{ color: GREEN }}>Citizen Outcomes</div>
              <ChevronRight size={11} className="text-[#7C93A5] ml-auto" />
            </div>
            <div className="font-display text-[15px] text-[#F6F8FA] font-bold mb-1">National Outcomes Centre™</div>
            <div className="text-[10.5px] text-[#B3C2CC] leading-relaxed">
              Citizen Impact Index · Transformation Index · Vision 2030 · Benefits Realisation · Social ·
              Environmental · Economic Impact. {xixCitizen?.length ? `${xixCitizen.length} live indicators.` : ""}
            </div>
          </Link>

          <Link to="/app/strategic-decisions" data-testid="ned-drill-strategic-decisions"
                className="rounded-sm border p-3 hover:brightness-125 transition-colors"
                style={{ borderColor: PURPLE + "50", background: "linear-gradient(135deg, rgba(7,27,43,0.6) 0%, " + PURPLE + "18 100%)" }}>
            <div className="flex items-center gap-2 mb-1">
              <Brain size={14} style={{ color: PURPLE }} />
              <div className="font-mono uppercase text-[10px] tracking-[0.22em]" style={{ color: PURPLE }}>Decision Intelligence</div>
              <ChevronRight size={11} className="text-[#7C93A5] ml-auto" />
            </div>
            <div className="font-display text-[15px] text-[#F6F8FA] font-bold mb-1">Strategic Decision Centre™</div>
            <div className="text-[10.5px] text-[#B3C2CC] leading-relaxed">
              Cabinet Advisor · Scenario · Risk · Policy · Trade-off Simulators · Executive Brief · Decision Memory.
              {xxCentre.length ? ` ${xxCentre.length} decisions await Cabinet.` : ""}
            </div>
          </Link>
        </div>
      </Card>

      {/* Assembled Pack Summary — appears after Assemble Meeting Pack™ */}
      {packSummary && (
        <Card title={`Meeting Pack Preview™ · ${packSummary.pack_id}`} accent={GOLD} testId="ned-pack-summary"
              right={<button onClick={() => setPackSummary(null)} className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]" data-testid="btn-close-pack-summary">Close</button>}>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-2 text-[11px]">
            <div className="rounded-sm border p-2" style={{ borderColor: GOLD + "30", background: "rgba(7,27,43,0.5)" }}>
              <div className="font-mono uppercase text-[9.5px] tracking-[0.16em]" style={{ color: GOLD }}>Presidential Brief</div>
              <div className="mt-1 text-[10.5px] text-[#F6F8FA]">
                {packSummary.presidential_brief?.executive_summary || "—"}
              </div>
              <div className="text-[9.5px] text-[#7C93A5] mt-1">
                Status · {packSummary.presidential_brief?.national_status || "—"}
              </div>
            </div>
            <div className="rounded-sm border p-2" style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
              <div className="font-mono uppercase text-[9.5px] tracking-[0.16em]" style={{ color: PURPLE }}>Governance Delivery (XVIII)</div>
              <div className="mt-1 text-[10.5px] text-[#F6F8FA]">
                Delivery {packSummary.governance_delivery?.programme_delivery ?? "—"}% · Revenue ${Number(packSummary.governance_delivery?.revenue ?? 0).toLocaleString()}
              </div>
              <div className="text-[9.5px] text-[#7C93A5] mt-1">
                Cabinet actions {packSummary.governance_delivery?.cabinet_actions ?? "—"}
              </div>
            </div>
            <div className="rounded-sm border p-2" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
              <div className="font-mono uppercase text-[9.5px] tracking-[0.16em]" style={{ color: GREEN }}>National Outcomes (XIX)</div>
              <div className="mt-1 text-[10.5px] text-[#F6F8FA]">
                {(packSummary.national_outcomes?.indicators || []).slice(0, 3).map(i => `${i.label} ${i.score}`).join(" · ") || "—"}
              </div>
            </div>
            <div className="rounded-sm border p-2" style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.5)" }}>
              <div className="font-mono uppercase text-[9.5px] tracking-[0.16em]" style={{ color: BLUE }}>Pending Decisions (XX)</div>
              <div className="mt-1 text-[10.5px] text-[#F6F8FA] space-y-0.5">
                {packSummary.pending_decisions.slice(0, 4).map((d) => (
                  <div key={d.decision_id} className="truncate">• {d.title}</div>
                ))}
                {packSummary.pending_decisions.length === 0 && <div className="italic text-[#7C93A5]">No pending items.</div>}
              </div>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Link to="/app/sovereign-performance" data-testid="pack-link-xviii"
                  className="rounded-sm border px-2 py-1 text-[10.5px]" style={{ borderColor: GOLD + "50", color: GOLD }}>
              Delivery Detail →
            </Link>
            <Link to="/app/national-outcomes" data-testid="pack-link-xix"
                  className="rounded-sm border px-2 py-1 text-[10.5px]" style={{ borderColor: GREEN + "50", color: GREEN }}>
              Outcomes Detail →
            </Link>
            <Link to="/app/strategic-decisions" data-testid="pack-link-xx"
                  className="rounded-sm border px-2 py-1 text-[10.5px]" style={{ borderColor: PURPLE + "50", color: PURPLE }}>
              Decision Centre →
            </Link>
            {packSummary.pack_url && (
              <a href={packSummary.pack_url} target="_blank" rel="noreferrer" data-testid="pack-download"
                 className="rounded-sm border px-2 py-1 text-[10.5px]" style={{ borderColor: PURPLE + "60", color: "#F6F8FA", background: PURPLE + "45" }}>
                Download signed PDF ↓
              </a>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default NationalExecutiveDashboard;
